<script setup>
/**
 * The one sentence that says why a message is where it is — when there is one.
 *
 * TWO SOURCES, ONE LINE, AND THEY ARE MUTUALLY EXCLUSIVE. A `suppressed` row carries a
 * `suppressedReason` and no error; a `failed` or `unconfirmed` row carries the upstream
 * error and no suppression reason. So one component covers both, and the list row and the
 * detail page cannot drift into two different explanations of the same message.
 *
 * `suppressedReason` IS NOT THE DO-NOT-CONTACT VOCABULARY, however alike the two look:
 * `opt_out | test_mode | recipient_cap` says which rule stopped THIS send, while a
 * suppression row's `manual | keyword | provider | api` says who put the number on the
 * list. They share no values and no meaning, which is why `api/client.js` keeps them in
 * two frozen lists and why the strings live under `REASON.MESSAGE.*` rather than beside
 * `REASON.LIST.*`.
 *
 * THE ERROR IS SOMEBODY ELSE'S TEXT. It is whatever WhatsApp or the send path said, of
 * whatever length, in whatever direction, with no obligation to contain a space — so it
 * wraps at any point, resolves its own direction, and is either clamped (in a row, where
 * three lines of upstream prose would bury the next message) or given its own scroll
 * container (on the detail page, where it is the answer). README §2: one long unbroken
 * token stretched a page to 2.6× the viewport once already.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** A row from `wasloApi.getOutboundMessages` or `getOutboundMessage`. */
  message: { type: Object, required: true },
  /** True in a list row: two lines, then an ellipsis. False: its own scrollport. */
  clamp: { type: Boolean, default: false },
});

const { t } = useWasloI18n();

const SUPPRESSED = {
  opt_out: 'OPT_OUT',
  test_mode: 'TEST_MODE',
  recipient_cap: 'RECIPIENT_CAP',
};

const text = computed(() => {
  const { suppressedReason, error, errorCode } = props.message;
  const key = suppressedReason === null ? null : SUPPRESSED[suppressedReason];
  // `REASON` IS A SIBLING OF `LOG`, NOT A CHILD OF IT — the tree is shared with the Do
  // not contact page, which reads the other half (`REASON.LIST.*`). Resolved from the
  // root for that reason: a `LOG.`-prefixed path resolves to nothing, and vue-i18n
  // answers a miss with the key string itself, so every suppressed row would render
  // `WASLO.OUTBOUND.LOG.REASON.MESSAGE.OPT_OUT` past a green suite and a clean lint.
  if (key) return t(`WASLO.OUTBOUND.REASON.MESSAGE.${key}`);
  // The code alone is a poor sentence, and it is still better than a blank line under a
  // heading that promised to say what went wrong.
  return error ?? errorCode ?? '';
});
</script>

<template>
  <p
    v-if="text"
    class="mb-0 text-sm leading-6 break-words whitespace-pre-wrap [overflow-wrap:anywhere] text-n-slate-11"
    :class="clamp ? 'line-clamp-2' : 'overflow-y-auto max-h-40'"
    dir="auto"
    data-waslo-outbound-reason
  >
    {{ text }}
  </p>
</template>
